import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { clearCounter, CounterKey, getCounter } from '@/lib/utils';
import { SaleTabKey } from '@/pages/sale/lib/sale-tab-config';
import { getPageId } from '@/services/utl-service';
import { useCallback, useState } from 'react';

type TabCounterKey = 'live' | 'inbox' | 'waiting';

const TAB_COUNTER_CLEAR_RULES: { tab: TabCounterKey; counterKey: CounterKey }[] = [
  { tab: 'waiting', counterKey: CounterKey.QUEUE },
  { tab: 'inbox', counterKey: CounterKey.INBOX },
  { tab: 'live', counterKey: CounterKey.LIVE },
];

type UseSaleTabCountersOptions = {
  onTabChange: (tab: SaleTabKey) => void;
  refetchConversation?: () => Promise<void>;
};

export function useSaleTabCounters({ onTabChange, refetchConversation }: UseSaleTabCountersOptions) {
  const [currentTabKey, setCurrentTabKey] = useState<SaleTabKey | null>(null);
  const [inboxCounter, setInboxCounter] = useState(() => getCounter(CounterKey.INBOX));
  const [queueCounter, setQueueCounter] = useState(() => getCounter(CounterKey.QUEUE));
  const [liveCounter, setLiveCounter] = useState(() => getCounter(CounterKey.LIVE));

  const tabCounters: Partial<Record<SaleTabKey, number>> = {
    live: liveCounter,
    inbox: inboxCounter,
    waiting: queueCounter,
  };

  const pageId = getPageId();

  const refreshInboxCounter = useCallback(() => {
    setInboxCounter(getCounter(CounterKey.INBOX));
  }, []);

  const refreshQueueCounter = useCallback(() => {
    setQueueCounter(getCounter(CounterKey.QUEUE));
  }, []);

  const refreshLiveCounter = useCallback(() => {
    setLiveCounter(getCounter(CounterKey.LIVE));
  }, []);

  useWebSocketChannel(`/topic/conversations/${pageId}`, refreshInboxCounter);
  useWebSocketChannel('/topic/q-customers', refreshQueueCounter);
  useWebSocketChannel('/topic/clear-q', refreshQueueCounter);
  useWebSocketChannel(`/topic/feed/${pageId}`, refreshLiveCounter);

  const resetCounter = useCallback((tab: TabCounterKey) => {
    if (tab === 'waiting') setQueueCounter(0);
    if (tab === 'inbox') setInboxCounter(0);
    if (tab === 'live') setLiveCounter(0);
  }, []);

  const handleTabChange = useCallback(
    async (tabKey: SaleTabKey) => {
      if (tabKey === 'inbox' && inboxCounter > 0 && refetchConversation) {
        await refetchConversation();
      }

      for (const { tab, counterKey } of TAB_COUNTER_CLEAR_RULES) {
        if (currentTabKey === tab && tabKey !== tab) {
          clearCounter(counterKey);
          resetCounter(tab);
        }
      }

      onTabChange(tabKey);
      setCurrentTabKey(tabKey);
    },
    [currentTabKey, inboxCounter, onTabChange, refetchConversation, resetCounter],
  );

  return { tabCounters, handleTabChange };
}
